import { message } from 'antd';
import { isAxiosError } from 'axios';
import qs from 'qs';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Tags/Columns';

import type { Tag } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';
import { getColumns, getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchCreate({ payload: item }: PayloadAction<Partial<Tag>>): Generator<Effect, void, Tag> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/tags', item);
    message.success('Étiquette créée avec succès.');

    yield call(Api.clearCache, /.*tags.*/);

    yield put(replace(`/tags/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouvelle étiquette'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<Tag>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));
  try {
    yield call(Api.destroy, '/tags', id);
    message.success('Étiquette supprimée avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({
  payload: params,
}: PayloadAction<IRestIndexParams<Tag>>): Generator<Effect, void, IndexResponse<Tag>> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/tags', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
    yield put(actions.setParams(currentParams));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

function* watchLoadParams(): Generator<Effect, void, any> {
  const currentParams = yield select(getParams);
  const params = qs.parse(document.location.search.substring(1));

  if (Object.keys(params).length === 0) {
    return;
  }

  for (const key in params) {
    let value = params[key];
    if (Array.isArray(value)) {
      value = value.join(',');
    }
    if (!value || typeof value !== 'string') {
      continue;
    }

    for (const k in currentParams) {
      switch (k) {
        case 'sortBy':
        case 'page':
        case 'pageSize':
        case 'with':
        case 'columns':
          break;
        default:
          if (!params[k]) {
            yield put(actions.unsetFilter(k as keyof Tag));
          }
          break;
      }
    }

    let parsed;
    switch (key) {
      case 'sortBy':
        if (params.sortBy !== currentParams.sortBy) {
          yield put(actions.setSortBy(params.sortBy));
        }
        break;
      case 'page':
        parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed) && parsed !== currentParams.page) {
          yield put(actions.setPage(parsed));
        }
        break;
      case 'pageSize':
        parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed) && parsed !== currentParams.pageSize) {
          yield put(actions.setPageSize(parsed));
        }
        break;
      case 'columns':
        parsed = value.split(',').filter((v) => !!v);
        if (parsed.length > 0) {
          yield put(actions.setColumns({
            view: 'index',
            columns: parsed as Array<keyof Tag>,
          }));
        }
        break;
      case 'with':
        break;
      default:
        if (typeof value === 'string' && params[key] !== currentParams[key]) {
          yield put(actions.setFilter({
              key: key as keyof Tag,
              value,
            }));
        }
        break;
    }
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: Tag['id'] | Tag['humanId']; params?: IRestIndexParams<Tag> }>): Generator<Effect, void, Tag> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));
    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/tags/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }
    yield put(app.title(item.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<Tag>>): Generator<Effect, void, any> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));

  const saveParams: Record<string, any> = {};

  const columns = yield select(getColumns);

  for (const key in params) {
    saveParams[key] = params[key as keyof Tag];
  }

  saveParams.columns = columns.join(',');

  yield put(
    replace({
      pathname: 'tags',
      search: qs.stringify(saveParams),
    }),
  );
}

export function* watchUpdate({ payload: item }: PayloadAction<Tag>): Generator<Effect, void, Tag> {
  if (!item.id) {
    return;
  }

  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/tags/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/tags/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(JSON.stringify(updatedItem)));
    message.success('Étiquette mise à jour avec succès.');

    yield call(Api.clearCache, /.*tags.*/);

    yield put(app.title(updatedItem.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(item.name));
    yield put(actions.setLoading(false));
  }
}

export function* watchTagsSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.loadParams, watchLoadParams);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.update, watchUpdate);
}

const tagsSagas = watchTagsSagas;

export default tagsSagas;
